import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Receipt, X } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { Profile } from '../../../types';
import LoadingSpinner from '../../../src/components/LoadingSpinner';

interface RotaApiLog {
    id: string;
    user_id?: string | null;
    paradas: number;
    chamadas_api: number;
    custo_estimado_usd: number;
    distancia_km?: number | null;
    duracao_min?: number | null;
    origem?: string | null;
    created_at: string;
}

const fmtUsd = (v: number) => `US$ ${v.toFixed(3).replace('.', ',')}`;
const fmtDur = (min?: number | null) => {
    if (!min) return '—';
    const h = Math.floor(min / 60);
    return h > 0 ? `${h}h${String(Math.round(min % 60)).padStart(2, '0')}` : `${Math.round(min)} min`;
};

interface RotasApiLogModalProps {
    isOpen: boolean;
    onClose: () => void;
    profiles: Profile[];
}

/** Registro de uso da Google Routes API (mesmo padrão de auditoria da Prospecção). */
const RotasApiLogModal: React.FC<RotasApiLogModalProps> = ({ isOpen, onClose, profiles }) => {
    const [logs, setLogs] = useState<RotaApiLog[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        const fetchLogs = async () => {
            setLoading(true);
            const { data, error } = await supabase
                .from('rotas_api_log')
                .select('id, user_id, paradas, chamadas_api, custo_estimado_usd, distancia_km, duracao_min, origem, created_at')
                .order('created_at', { ascending: false })
                .limit(100);
            if (error) console.error(error);
            setLogs((data || []) as RotaApiLog[]);
            setLoading(false);
        };
        fetchLogs();
    }, [isOpen]);

    const totais = useMemo(() => {
        const iniMes = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
        const doMes = logs.filter(l => new Date(l.created_at) >= iniMes);
        return {
            mesOtimizacoes: doMes.length,
            mesCusto: doMes.reduce((s, l) => s + Number(l.custo_estimado_usd || 0), 0),
            totalCusto: logs.reduce((s, l) => s + Number(l.custo_estimado_usd || 0), 0),
        };
    }, [logs]);

    const nome = (id?: string | null) => profiles.find(p => String(p.id) === String(id))?.full_name || '—';

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
            <div
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2.5">
                        <div className="p-2 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-xl">
                            <Receipt size={18} />
                        </div>
                        <div>
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white">Registro de Uso da API — Rotas</h2>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400">Google Routes · ~US$ 0,010 por otimização com trânsito</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                        <X size={18} />
                    </button>
                </div>

                <div className="grid grid-cols-3 gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="rounded-xl bg-gray-50 dark:bg-gray-700/40 p-3">
                        <p className="text-[10px] font-semibold text-gray-400 uppercase">Otimizações no mês</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">{totais.mesOtimizacoes}</p>
                    </div>
                    <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 p-3">
                        <p className="text-[10px] font-semibold text-emerald-600/70 dark:text-emerald-400/70 uppercase">Custo no mês</p>
                        <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300">{fmtUsd(totais.mesCusto)}</p>
                    </div>
                    <div className="rounded-xl bg-gray-50 dark:bg-gray-700/40 p-3">
                        <p className="text-[10px] font-semibold text-gray-400 uppercase">Custo total (100 últ.)</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">{fmtUsd(totais.totalCusto)}</p>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-3">
                    {loading ? (
                        <div className="flex justify-center py-10"><LoadingSpinner label="Carregando registros..." size="sm" /></div>
                    ) : logs.length === 0 ? (
                        <p className="text-center text-sm text-gray-400 py-10">Nenhuma otimização registrada ainda.</p>
                    ) : (
                        <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-white dark:bg-gray-800">
                                <tr className="text-left text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                    <th className="py-2 pr-3 font-semibold">Quando</th>
                                    <th className="py-2 pr-3 font-semibold">Quem</th>
                                    <th className="py-2 pr-3 font-semibold">Origem</th>
                                    <th className="py-2 pr-3 font-semibold text-right">Paradas</th>
                                    <th className="py-2 pr-3 font-semibold text-right">Distância</th>
                                    <th className="py-2 pr-3 font-semibold text-right">Duração</th>
                                    <th className="py-2 font-semibold text-right">Custo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map(l => (
                                    <tr key={l.id} className="border-b border-gray-50 dark:border-gray-700/50">
                                        <td className="py-2 pr-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                            {new Date(l.created_at).toLocaleDateString('pt-BR')} {new Date(l.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                        </td>
                                        <td className="py-2 pr-3 font-semibold text-gray-800 dark:text-gray-100">{nome(l.user_id)}</td>
                                        <td className="py-2 pr-3 text-gray-500 dark:text-gray-400">{l.origem === 'roteiro_diario' ? 'Roteiro Diário' : l.origem === 'inteligente' ? 'Rota Inteligente' : l.origem === 'mapa' ? 'Prospecção' : 'Roteirização'}</td>
                                        <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-300">{l.paradas}</td>
                                        <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-300">{l.distancia_km ? `${Math.round(l.distancia_km)} km` : '—'}</td>
                                        <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-300">{fmtDur(l.duracao_min)}</td>
                                        <td className="py-2 text-right font-bold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">{fmtUsd(Number(l.custo_estimado_usd || 0))}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default RotasApiLogModal;
